import React from 'react';

import LongTextValueEditor from 'metadata/LongTextValueEditor';
import notifications from 'notifications';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {
  getDisplayOperatorAndOperatorDropdown,
  getValueNodesFactory,
} from 'search/filters/base/StandardFilterFormComponents';
import {MultiLinkSchemaFieldFilterValueNode} from 'search/filters/MultiLinkSchemaFieldFilterValueNode';
import {standardOperatorsForFieldType} from 'search/FilterUtil';
import {
  FieldFilter,
  isMultiLinkFieldFilter,
  isNumericFieldFilter,
  isStringFieldFilter,
  MultiLinkFieldFilter,
  Operator,
  RELATIVE_RANGE_SHORTCUT_OPERATORS,
  STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY,
  StandardFilterDataType,
  StringFieldFilter,
  VALUELESS_OPERATORS,
} from 'search/SearchConstants';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import SchemaFieldFilterFormSkeleton from './base/SchemaFieldFilterFormSkeleton';

export interface SchemaFieldFilterFormProps extends BaseFilterFormProps {
  filter: FieldFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: FieldFilter) => void;
  onRemove?: () => void;
  allowBlobLinkFields?: boolean;
}

export interface SchemaFieldFilterFormExtraProps {
  allowBlobLinkFields?: boolean;
  schemaFields?: Array<SchemaField>;
}

export default function SchemaFieldStandardFilterForm(
  props: SchemaFieldFilterFormProps & SchemaFieldFilterFormExtraProps
): JSX.Element {
  const _handleChangeSchemaField = (schemaField: SchemaField): void => {
    const standardOperators = standardOperatorsForFieldType(schemaField.fieldType);
    if (standardOperators.length === 0) {
      notifications.error(
        `Filtering on the field type of '${schemaField.displayName}' is not currently supported. Please try another field.`
      );
      return;
    }
    const operator = standardOperators[0];
    const defaultValueFactory = STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY[operator];
    if (!defaultValueFactory) {
      throw new Error(
        `Attempted to select operator ${operator} for standard filter, but no default value factory was found.`
      );
    }
    props.onChangeFilter({
      ...props.filter,
      fieldId: schemaField.apiId,
      // @ts-expect-error TODO(BNCH-56923): This assignment is not correct since schemaField.fieldType is of
      // type FieldType, but the existing FieldType filters do not support all FieldTypes.
      // You can see which are unsupported by doing `type X = Exclude<FieldType, FieldFilter['fieldType]>`
      // then hovering `X`.
      fieldType: schemaField.fieldType,
      operator,
      value: defaultValueFactory(),
    });
  };

  const selectedSchemaField = props.schemaFields
    ? props.schemaFields.find((schemaField) => schemaField.apiId === props.filter.fieldId)
    : null;
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const sharedProps = {
    ...skeletonProps,
    filterStyle: props.filterStyle,
    onChangeFilter: props.onChangeFilter,
    filter: props.filter,
    onChangeSchemaField: (schemaField: SchemaField) => {
      _handleChangeSchemaField(schemaField);
    },
    onRemove: props.onRemove,
    operators: standardOperatorsForFieldType(props.filter.fieldType),
    schemaFields: props.schemaFields ? props.schemaFields : [],
    selectedSchemaField: selectedSchemaField || null,
    allowBlobLinkFields: props.allowBlobLinkFields,
  };

  const [displayOperator, operatorDropdown] = getDisplayOperatorAndOperatorDropdown(
    sharedProps.filter,
    sharedProps.onChangeFilter,
    sharedProps.buttonSize,
    sharedProps.operators,
    props.isDirty
  );

  if (
    !selectedSchemaField ||
    VALUELESS_OPERATORS.includes(displayOperator) ||
    RELATIVE_RANGE_SHORTCUT_OPERATORS.includes(displayOperator)
  ) {
    return <SchemaFieldFilterFormSkeleton {...sharedProps} operatorDropdown={operatorDropdown} />;
  }
  const standardFilterFormProps = {
    ...sharedProps,
    filter: props.filter,
    filterStyle: props.filterStyle,
    onChangeFilter: props.onChangeFilter,
  };
  let valueNodes;
  if (isNumericFieldFilter(props.filter)) {
    switch (props.filter.fieldType) {
      case FieldType.INTEGER:
        valueNodes = getValueNodesFactory(StandardFilterDataType.INT)(standardFilterFormProps);
        break;
      case FieldType.FLOAT:
        valueNodes = getValueNodesFactory(StandardFilterDataType.FLOAT)(standardFilterFormProps);
        break;
      default:
        break;
    }
  }
  if (isStringFieldFilter(props.filter)) {
    switch (props.filter.fieldType) {
      case FieldType.DATE:
        valueNodes = getValueNodesFactory(StandardFilterDataType.DATE)(standardFilterFormProps);
        break;
      case FieldType.DATETIME:
        valueNodes = getValueNodesFactory(StandardFilterDataType.DATETIME)(standardFilterFormProps);
        break;
      case FieldType.TEXT:
        valueNodes = getValueNodesFactory(StandardFilterDataType.STRING)(standardFilterFormProps);
        break;
      case FieldType.LONG_TEXT:
        valueNodes = [
          <LongTextValueEditor
            autoFocus={false}
            defaultValue={{
              name: selectedSchemaField.name,
              tagSchemaField: selectedSchemaField,
              value: props.filter.value as string,
            }}
            onChange={({value}: {value: string | null}) => {
              // Backward compatibility
              let convertedValue: Array<string> | string | null = value;
              if ([Operator.IS, Operator.IS_NOT].includes(props.filter.operator)) {
                if (value === '') {
                  convertedValue = null;
                }
              } else if (
                [
                  Operator.INCLUDES_TEXT,
                  Operator.EXCLUDES_TEXT,
                  Operator.STARTSWITH_TEXT,
                  Operator.IS_ONE_OF,
                  Operator.IS_NONE_OF,
                ].includes(props.filter.operator)
              ) {
                if (value === '') {
                  convertedValue = [];
                } else if (value) {
                  convertedValue = value.split(',');
                }
              }

              props.onChangeFilter({...(props.filter as StringFieldFilter), value: convertedValue});
            }}
            placeholder="Text"
          />,
        ];
        break;
      default:
        break;
    }
  }

  if (isMultiLinkFieldFilter(props.filter)) {
    valueNodes = [
      <MultiLinkSchemaFieldFilterValueNode
        {...standardFilterFormProps}
        onChangeValue={(value: Array<string>) => {
          props.onChangeFilter({...(props.filter as MultiLinkFieldFilter), value});
        }}
        selectedSchemaField={selectedSchemaField}
        filter={props.filter}
        value={props.filter.value as Array<string>}
      />,
    ];
  }

  if (!valueNodes) {
    throw new Error(`Unsupported field type ${props.filter.fieldType}`);
  }

  return (
    <SchemaFieldFilterFormSkeleton
      {...sharedProps}
      operatorDropdown={operatorDropdown}
      valueNodes={valueNodes}
    />
  );
}
